import { createSlice } from '@reduxjs/toolkit';

import { authApi } from './authApi';

interface successLoginResp {
  email: string;
  message: string;
  firstName: string;
  lastName: string;
  lifecycleStatus: string;
  role: string;
  roleId: string;
  tenantId: string;
  token: string;
  teamId: string;
  id: string;
  tenant: object;
  ProfileURL: string;
  userExtId: string;
  userExtPassword: string;
  extNumber: string;
  permissions: {} | null;
  customFieldModules: [] | string[];
  messageSignature?: string;
}

interface AuthState {
  registerApiData: RegisterApiResponse | null;
  loginApiData: null | successLoginResp | { email: string; message: string };
  verifyEmailApiData: null | { email: string; message: string };
  channelFeatureFlags: Record<string, boolean>;
  channelFeatureFlagsInitialized: boolean;
  roleId: string | undefined;
}

interface RegisterApiResponse {
  id: string;
  tenantId: string;
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role: string;
  teamId: string | null;
}

const initialState: AuthState = {
  registerApiData: null,
  verifyEmailApiData: null,
  loginApiData: null,
  channelFeatureFlags: {},
  channelFeatureFlagsInitialized: false,
  roleId: undefined,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setRegisterApiData: (state, action) => {
      state.registerApiData = action.payload;
    },
    setLoginApiData: (state, action) => {
      state.loginApiData = action.payload;
    },
    setVerifyEmailApiData: (state, action) => {
      state.verifyEmailApiData = action.payload;
    },
    setChannelFeatureFlags: (state, action) => {
      state.channelFeatureFlags = action.payload;
      state.channelFeatureFlagsInitialized = true;
    },
    updateUserRole: (
      state,
      action: { payload: { role?: string; roleId?: string; roles?: any[]; permissions?: any } }
    ) => {
      const newRole = action.payload.role || action.payload.roles?.[0]?.name || 'Admin';
      const newRoleId = action.payload.roleId || action.payload.roles?.[0]?.id || '';

      if (state.loginApiData && typeof state.loginApiData === 'object') {
        state.loginApiData = {
          ...state.loginApiData,
          role: newRole,
          ...(newRoleId && { roleId: newRoleId }),
          ...(action.payload.permissions && { permissions: action.payload.permissions }),
        };
      }
      state.roleId = newRoleId || state.roleId;

      try {
        const persistRoot = localStorage.getItem('persist:root');
        if (persistRoot) {
          const parsed = JSON.parse(persistRoot);
          if (parsed.auth) {
            const authParsed = JSON.parse(parsed.auth);
            if (authParsed.loginApiData) {
              authParsed.loginApiData.role = newRole;
              if (newRoleId) authParsed.loginApiData.roleId = newRoleId;
              if (action.payload.permissions) {
                authParsed.loginApiData.permissions = action.payload.permissions;
              }
              parsed.auth = JSON.stringify(authParsed);
              localStorage.setItem('persist:root', JSON.stringify(parsed));
            }
          }
        }
      } catch (e) {
        console.error('Failed to sync updated role to persist:root', e);
      }
    },
    setAuthStateInitial: (state) => {
      state.registerApiData = null;
      state.loginApiData = null;
      state.verifyEmailApiData = null;
      state.channelFeatureFlags = {};
      state.channelFeatureFlagsInitialized = false;
      state.roleId = undefined;
    },
  },
  extraReducers: (builder) => {
    // Register User
    builder.addMatcher(authApi.endpoints.registerUser.matchFulfilled, (state, action) => {
      const { id, tenantId, email, password, firstName, lastName, role, teamId } =
        action.payload.data.user;
      const registerData = { id, tenantId, email, password, firstName, lastName, role, teamId };
      state.registerApiData = registerData;
    });
    // Login User
    builder.addMatcher(authApi.endpoints.loginUser.matchFulfilled, (state, action) => {
      const successMessages = ['Request successful', 'Login successful'];

      if (action.payload.success && successMessages.includes(action.payload.message)) {
        const user = action.payload.data.user;

        state.loginApiData = {
          firstName: user.firstName ?? '',
          lastName: user.lastName ?? '',
          role: user.role ?? '',
          roleId: user.roleId ?? user.roles?.[0]?.id ?? '',
          lifecycleStatus: user?.tenant?.tenantSetting
            ? user?.tenant?.tenantSetting[0]?.showLifecycleStages
            : '',
          email: user.email ?? '',
          tenantId: user.tenantId ?? '',
          token: action.payload.data.token ?? user.token ?? '',
          message: action.payload.message ?? '',
          teamId: user.teamId ?? '',
          id: user.id ?? '',
          tenant: action.payload.data.tenant ?? user.tenant ?? '',
          ProfileURL: user.ProfileURL ?? '',
          userExtId: user.userExtId ?? '',
          userExtPassword: user.userExtPassword ?? '',
          extNumber: user.extNumber ?? '',
          permissions: action?.payload?.data?.permissions ?? user?.permissions ?? null,
          customFieldModules: user.moduleFields ?? [],
          messageSignature: user.messageSignature ?? '',
        };
        state.roleId = user.roleId ?? user.roles?.[0]?.id ?? '';
      } else {
        console.log('🔥 Login not successful or message mismatch:', {
          success: action.payload.success,
          message: action.payload.message,
        });
      }
    });
    // auto login
    builder.addMatcher(authApi.endpoints.autoLoginUser.matchFulfilled, (state, action) => {
      if (action.payload.success && action.payload.message === 'Login successful') {
        const user = action.payload.data.user;
        state.loginApiData = {
          firstName: user.firstName ?? '',
          lastName: user.lastName ?? '',
          role: user.role ?? '',
          email: user.email ?? '',
          tenantId: user.tenantId ?? '',
          token: action.payload.data.token ?? user.token ?? '',
          message: action.payload.message ?? '',
          teamId: user.teamId ?? '',
          id: user.id ?? '',
          tenant: action.payload.data.tenant ?? user.tenant ?? '',
          ProfileURL: user.ProfileURL ?? '',
          userExtId: user.userExtId ?? '',
          userExtPassword: user.userExtPassword ?? '',
          extNumber: user.extNumber ?? '',
          lifecycleStatus: user?.tenant?.tenantSetting[0]?.showLifecycleStages,
          permissions: action?.payload?.data?.permissions ?? user.permissions ?? null,
          messageSignature: user.messageSignature ?? '',
          roleId: user.roleId ?? user.roles?.[0]?.id ?? '',
        };
        state.roleId = user.roleId ?? user.roles?.[0]?.id ?? '';
      }
    });
    builder.addMatcher(authApi.endpoints.loginUser.matchRejected, (state, action) => {
      const reqParams = action?.meta?.arg.originalArgs;
      const payload = action.payload as { data: { message: string } };
      state.loginApiData = { email: reqParams?.email, message: payload?.data?.message };
    });
    builder.addMatcher(authApi.endpoints.verifyEmail.matchFulfilled, (state, action) => {
      const reqParams = action?.meta?.arg.originalArgs;
      state.verifyEmailApiData = { email: reqParams?.email, message: 'Code Sent Successfully' };
    });
  },
});

export default authSlice.reducer;
export const {
  setRegisterApiData,
  setVerifyEmailApiData,
  setLoginApiData,
  setChannelFeatureFlags,
  setAuthStateInitial,
  updateUserRole,
} = authSlice.actions;
